import React, { useState } from "react";
import {
  X,
  Bell,
  CheckCircle,
  AlertCircle,
  Info,
  Calendar,
  Trash2,
} from "lucide-react";
import { Button } from "./ui/Button";

interface Notification {
  id: string;
  type: "success" | "warning" | "info" | "booking";
  title: string;
  message: string;
  time: string;
  read: boolean;
}

interface NotificationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialNotifications: Notification[] = [
  {
    id: "1",
    type: "booking",
    title: "New Booking",
    message: "Sarah Johnson booked a haircut for tomorrow at 2:00 PM",
    time: "2 min ago",
    read: false,
  },
  {
    id: "2",
    type: "success",
    title: "Payment Received",
    message: "Payment of $85.00 received from Michael Chen",
    time: "15 min ago",
    read: false,
  },
  {
    id: "3",
    type: "warning",
    title: "Appointment Reminder",
    message: "You have 3 appointments scheduled for today",
    time: "1 hour ago",
    read: false,
  },
  {
    id: "4",
    type: "info",
    title: "New Customer",
    message: "Emma Wilson just signed up and is browsing services",
    time: "2 hours ago",
    read: true,
  },
  {
    id: "5",
    type: "success",
    title: "Review Posted",
    message: "Jessica Martinez left a review",
    time: "5 hours ago",
    read: true,
  },
  {
    id: "6",
    type: "booking",
    title: "Booking Cancelled",
    message: "David Lee cancelled their appointment",
    time: "1 day ago",
    read: true,
  },
  {
    id: "7",
    type: "info",
    title: "Inventory Alert",
    message: "Hair gel stock is running low",
    time: "2 days ago",
    read: true,
  },
];

const NotificationSidebar: React.FC<NotificationSidebarProps> = ({
  isOpen,
  onClose,
}) => {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [visibleCount, setVisibleCount] = useState(5);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type: Notification["type"]) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-orange-400" />;
      case "info":
        return <Info className="w-5 h-5 text-blue-500" />;
      case "booking":
        return <Calendar className="w-5 h-5 text-[#885ABB]" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  // Actions
  const handleMarkAllRead = () =>
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  const handleClearAll = () => setNotifications([]);
  const handleDeleteOne = (id: string) =>
    setNotifications(notifications.filter((n) => n.id !== id));
  const markOneRead = (id: string) =>
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  const loadMore = () =>
    setVisibleCount((prev) => Math.min(prev + 5, notifications.length));

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[600px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-[#885ABB] z-50 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2 text-white">
                <Bell className="w-6 h-6 text-white" /> Notifications
              </h2>
              <p className="text-sm text-white/80 mt-1">
                {unreadCount} unread{" "}
                {unreadCount === 1 ? "notification" : "notifications"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-md"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {notifications.length > 0 && (
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleMarkAllRead}
                className="flex-1 bg-white  text-[#885ABB] font-medium transition px-4 py-2 rounded-lg text-sm"
              >
                Mark all read
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 bg-white  text-gray-700 font-medium transition px-4 py-2 rounded-lg text-sm"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto h-[calc(100vh-150px)] p-4 custom-scrollbar">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
              <Bell className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">No notifications</p>
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {notifications.slice(0, visibleCount).map((n) => (
                  <div
                    key={n.id}
                    onClick={() => markOneRead(n.id)}
                    className={`group relative rounded-md p-4 border  cursor-pointer flex items-start gap-3 ${
                      !n.read
                        ? "border-[#885ABB]/40 bg-white "
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    {/* Left border for unread */}
                    {!n.read && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#885ABB] rounded-l-xl" />
                    )}

                    <div className="flex-shrink-0 mt-1">
                      <div className="bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                        {getNotificationIcon(n.type)}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-sm mb-1 ${
                          !n.read ? "font-semibold" : "font-normal"
                        } text-gray-800`}
                      >
                        {n.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{n.message}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 font-medium">
                          {n.time}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOne(n.id);
                          }}
                          className="opacity-100  p-1.5 rounded-md"
                        >
                          <Trash2 className="w-4 h-4 text-red-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load more button */}
              {visibleCount < notifications.length && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={loadMore}
                    className="px-4 py-2 bg-[#885ABB] hover:bg-[#7649A2] text-white rounded-lg font-medium transition"
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Custom Scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #885ABB;
          border-radius: 10px;
        }
      `}</style>
    </>
  );
};

export default NotificationSidebar;
